import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditPayment,
  setEditPayment,
  toggleCreateModal,
} from "../../features/payments/paymentsSlice";
import { IFormInputPayment } from "../../types/payments";
import { useParams } from "react-router-dom";
import { t } from "../../locales";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2 } from "lucide-react";

type Props = {
  handleSavePayment: (data: IFormInputPayment) => void;
};

const PaymentForm = ({ handleSavePayment }: Props) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const editPayment = useAppSelector(getEditPayment);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<IFormInputPayment>({
    defaultValues: { amount: null, note: "" },
  });

  const handleOnSubmit: SubmitHandler<IFormInputPayment> = async (data: any) => {
    setIsLoading(true);
    if (editPayment && editPayment.id) {
      await handleSavePayment({ ...data, id: editPayment.id, type: 1 });
    } else {
      await handleSavePayment({ ...data, type: 1, party_id: id });
    }
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleCreateModal(0));
    dispatch(setEditPayment(null));
  };

  useEffect(() => {
    if (editPayment && editPayment.id) {
      reset({ amount: editPayment.amount, note: editPayment.note });
    } else {
      reset({ amount: null, note: "" });
    }
  }, [editPayment]);

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) handleCloseFormModal(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">{t.payments.forms.receivedTitle}</DialogTitle>
          <DialogDescription>{t.payments.actions.receivedPayment}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4 flex-1 flex flex-col sm:block pb-16 sm:pb-0 sm:space-y-5">
          <div className="space-y-2">
            <Label>{t.payments.formFields.amount}</Label>
            <Input
              type="number"
              step="any"
              placeholder={t.payments.formFields.amount}
              {...register("amount", { required: true, min: 0 })}
              className="h-12"
              onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
            />
            {errors.amount && <span className="text-red-500 text-sm">{t.payments.formFields.amount}</span>}
          </div>

          <div className="space-y-2">
            <Label>{t.payments.formFields.note}</Label>
            <Input
              type="text"
              placeholder={t.payments.formFields.note}
              {...register("note")}
              className="h-12"
            />
            {errors.note && <span className="text-red-500 text-sm">{t.payments.formFields.note}</span>}
          </div>

          <div className="fixed bottom-0 left-0 right-0 flex gap-3 p-4 justify-end border-t bg-white z-10 sm:static sm:p-0 sm:pt-2 sm:z-auto sm:border-t sm:bg-transparent">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              {t.payments.buttons.close}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
            >
              {isLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t.payments.buttons.saving}</> : t.payments.buttons.continue}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentForm;
